import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  MenuItem,
  Container,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Email as EmailIcon,
  VerifiedUser as VerifiedIcon,
  ConfirmationNumber as TicketIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

// GraphQL Operations
const REQUEST_OTP = gql`
  mutation RequestTicketOTP($email: String!, $spaceId: ID!) {
    requestTicketOTP(email: $email, spaceId: $spaceId) {
      success
      message
    }
  }
`;

const VERIFY_OTP = gql`
  mutation VerifyTicketOTP($email: String!, $otp: String!, $spaceId: ID!) {
    verifyTicketOTP(email: $email, otp: $otp, spaceId: $spaceId) {
      success
      message
      token
    }
  }
`;

const CREATE_PUBLIC_TICKET = gql`
  mutation CreatePublicTicket($input: CreatePublicTicketInput!) {
    createPublicTicket(input: $input) {
      success
      message
      ticket {
        id
        ticketNumber
        subject
        status
        priority
        createdAt
      }
    }
  }
`;

const GET_PUBLIC_TICKETS = gql`
  query GetPublicTickets($email: String!, $spaceId: ID!, $token: String!) {
    getPublicTickets(email: $email, spaceId: $spaceId, token: $token) {
      id
      ticketNumber
      subject
      status
      priority
      createdAt
      updatedAt
    }
  }
`;

const GET_PUBLIC_TICKET = gql`
  query GetPublicTicket($ticketId: ID!, $token: String!) {
    getPublicTicket(ticketId: $ticketId, token: $token) {
      id
      ticketNumber
      subject
      description
      status
      priority
      assignedTo {
        name
      }
      messages {
        id
        senderName
        content
        timestamp
      }
      createdAt
      updatedAt
    }
  }
`;

const ADD_PUBLIC_TICKET_MESSAGE = gql`
  mutation AddPublicTicketMessage(
    $ticketId: ID!
    $content: String!
    $token: String!
  ) {
    addPublicTicketMessage(
      ticketId: $ticketId
      content: $content
      token: $token
    ) {
      id
      messages {
        id
        senderName
        content
        timestamp
      }
    }
  }
`;

const steps = ["Verify Email"];

function PublicSupportTicket() {
  const { spaceId } = useParams();

  // Session keys
  const SESSION_EMAIL_KEY = `supportTicket_email_${spaceId}`;
  const SESSION_TOKEN_KEY = `supportTicket_token_${spaceId}`;

  // State
  const [view, setView] = useState("verify"); // 'verify' | 'my-tickets' | 'create-ticket' | 'ticket-detail'
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [token, setToken] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicketData, setNewTicketData] = useState({
    subject: "",
    description: "",
    priority: "Medium",
  });
  const [replyMessage, setReplyMessage] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // GraphQL Mutations
  const [requestOTP, { loading: otpLoading }] = useMutation(REQUEST_OTP);
  const [verifyOTP, { loading: verifyLoading }] = useMutation(VERIFY_OTP);
  const [createTicket, { loading: createLoading }] =
    useMutation(CREATE_PUBLIC_TICKET);
  const [addMessage, { loading: addMessageLoading }] = useMutation(
    ADD_PUBLIC_TICKET_MESSAGE
  );

  // GraphQL Queries
  const [
    fetchTickets,
    {
      data: ticketsData,
      loading: ticketsLoading,
      refetch: refetchTickets,
      error: ticketsError,
    },
  ] = useLazyQuery(GET_PUBLIC_TICKETS, {
    variables: { email, spaceId, token },
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error("Tickets query error:", error);
      if (error.message.includes("Invalid or expired token")) {
        setError("Your session has expired. Please log in again.");
        setTimeout(() => handleLogout(), 2000);
      } else {
        setError(`Error loading tickets: ${error.message}`);
      }
    },
  });

  const [fetchTicket, { data: ticketData, loading: ticketLoading }] =
    useLazyQuery(GET_PUBLIC_TICKET, {
      fetchPolicy: "network-only",
    });

  // Check for existing session on mount
  useEffect(() => {
    const savedEmail = sessionStorage.getItem(SESSION_EMAIL_KEY);
    const savedToken = sessionStorage.getItem(SESSION_TOKEN_KEY);

    if (savedEmail && savedToken) {
      setEmail(savedEmail);
      setToken(savedToken);
      setView("my-tickets");
      setActiveStep(1);
    }
  }, [spaceId]);

  // Fetch tickets when view is my-tickets
  useEffect(() => {
    if (view === "my-tickets" && email && token) {
      fetchTickets().catch((error) => {
        console.error("Error fetching tickets:", error);
        if (error.message && error.message.includes("expired")) {
          setError("Your session has expired. Please verify your email again.");
          setTimeout(() => handleLogout(), 2000);
        }
      });
    }
  }, [view, email, token]);

  // Handle OTP Request
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      const { data } = await requestOTP({
        variables: { email, spaceId },
      });

      if (data.requestTicketOTP.success) {
        setSuccess(data.requestTicketOTP.message);
        setActiveStep(1);
      } else {
        setError(data.requestTicketOTP.message);
      }
    } catch (err) {
      console.error("Error requesting OTP:", err);
      setError("Failed to send verification code. Please try again.");
    }
  };

  // Handle OTP Verification
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (otp.length !== 6) {
      setError("Please enter a 6-digit code");
      return;
    }

    try {
      const { data } = await verifyOTP({
        variables: { email, otp, spaceId },
      });

      if (data.verifyTicketOTP.success) {
        const newToken = data.verifyTicketOTP.token;
        setToken(newToken);

        // Save to sessionStorage
        sessionStorage.setItem(SESSION_EMAIL_KEY, email);
        sessionStorage.setItem(SESSION_TOKEN_KEY, newToken);

        setSuccess("Email verified successfully!");
        setView("my-tickets");
      } else {
        setError(data.verifyTicketOTP.message);
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setError("Verification failed. Please try again.");
    }
  };

  // Handle Create Ticket
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!newTicketData.subject || !newTicketData.description) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      const { data } = await createTicket({
        variables: {
          input: {
            ...newTicketData,
            spaceId,
            token,
          },
        },
      });

      if (data.createPublicTicket.success) {
        setSuccess("Ticket created successfully!");
        setNewTicketData({
          subject: "",
          description: "",
          priority: "Medium",
        });
        // Refresh tickets list
        refetchTickets();
        setView("my-tickets");
      } else {
        setError(data.createPublicTicket.message);
      }
    } catch (err) {
      console.error("Error creating ticket:", err);
      setError("Failed to create ticket. Please try again.");
    }
  };

  // Handle Add Message
  const handleAddMessage = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!replyMessage.trim()) {
      setError("Please enter a message");
      return;
    }

    try {
      await addMessage({
        variables: {
          ticketId: selectedTicket.id,
          content: replyMessage,
          token,
        },
      });

      setSuccess("Message sent!");
      setReplyMessage("");

      // Refresh ticket details
      fetchTicket({
        variables: { ticketId: selectedTicket.id, token },
      });
    } catch (err) {
      console.error("Error adding message:", err);
      setError("Failed to send message. Please try again.");
    }
  };

  // View Ticket Detail
  const viewTicketDetail = async (ticket) => {
    setSelectedTicket(ticket);
    setView("ticket-detail");

    try {
      await fetchTicket({
        variables: { ticketId: ticket.id, token },
      });
    } catch (err) {
      console.error("Error fetching ticket:", err);
      setError("Failed to load ticket details");
    }
  };

  // Logout
  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_EMAIL_KEY);
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    setEmail("");
    setToken("");
    setOtp("");
    setView("verify");
    setActiveStep(0);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "Open":
        return "error";
      case "In Progress":
        return "warning";
      case "Resolved":
        return "success";
      case "Closed":
        return "default";
      default:
        return "default";
    }
  };

  // Safe date formatter
  const formatDate = (date, formatStr) => {
    try {
      if (!date) return "N/A";

      // If it's a string that looks like a timestamp (all digits), convert to number
      let dateValue = date;
      if (typeof date === "string" && /^\d+$/.test(date)) {
        dateValue = parseInt(date, 10);
      }

      const parsedDate = new Date(dateValue);
      if (isNaN(parsedDate.getTime())) return "N/A";

      return format(parsedDate, formatStr);
    } catch (error) {
      console.error("Date formatting error:", error, "for date:", date);
      return "N/A";
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h3" gutterBottom>
            Support Portal
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {view === "verify" && "Verify your email to continue"}
            {view === "my-tickets" && `Welcome, ${email}`}
            {view === "create-ticket" && "Create a new support ticket"}
            {view === "ticket-detail" && "Ticket Details"}
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Email Verification View */}
        {view === "verify" && (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && (
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <EmailIcon
                      sx={{ mr: 2, fontSize: 40, color: "primary.main" }}
                    />
                    <Box>
                      <Typography variant="h5">Verify Your Email</Typography>
                      <Typography variant="body2" color="text.secondary">
                        We'll send you a verification code
                      </Typography>
                    </Box>
                  </Box>

                  <form onSubmit={handleRequestOTP}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                      sx={{ mb: 3 }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={otpLoading}
                      startIcon={otpLoading && <CircularProgress size={20} />}
                    >
                      {otpLoading ? "Sending..." : "Send Verification Code"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {activeStep === 1 && (
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <VerifiedIcon
                      sx={{ mr: 2, fontSize: 40, color: "primary.main" }}
                    />
                    <Box>
                      <Typography variant="h5">
                        Enter Verification Code
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Check your email for the 6-digit code
                      </Typography>
                    </Box>
                  </Box>

                  <form onSubmit={handleVerifyOTP}>
                    <TextField
                      fullWidth
                      label="Verification Code"
                      value={otp}
                      onChange={(e) =>
                        setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                      required
                      autoFocus
                      sx={{ mb: 2 }}
                      inputProps={{ maxLength: 6 }}
                    />

                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setActiveStep(0)}
                        fullWidth
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={verifyLoading || otp.length !== 6}
                        startIcon={
                          verifyLoading && <CircularProgress size={20} />
                        }
                      >
                        {verifyLoading ? "Verifying..." : "Verify Code"}
                      </Button>
                    </Box>
                  </form>

                  <Button
                    variant="text"
                    size="small"
                    onClick={handleRequestOTP}
                    sx={{ mt: 2 }}
                    disabled={otpLoading}
                  >
                    Resend Code
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* My Tickets View */}
        {view === "my-tickets" && (
          <Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
            >
              <Typography variant="h5">My Support Tickets</Typography>
              <Box>
                <IconButton
                  onClick={() => refetchTickets()}
                  disabled={ticketsLoading}
                >
                  <RefreshIcon />
                </IconButton>
                <Button
                  variant="outlined"
                  onClick={handleLogout}
                  sx={{ ml: 1 }}
                >
                  Logout
                </Button>
              </Box>
            </Box>

            <Button
              variant="contained"
              startIcon={<TicketIcon />}
              onClick={() => setView("create-ticket")}
              fullWidth
              sx={{ mb: 3 }}
            >
              Create New Ticket
            </Button>

            {ticketsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : ticketsData?.getPublicTickets?.length === 0 ? (
              <Alert severity="info">
                No tickets yet. Create your first support ticket!
              </Alert>
            ) : (
              <List>
                {ticketsData?.getPublicTickets?.map((ticket) => (
                  <React.Fragment key={ticket.id}>
                    <ListItem
                      button
                      onClick={() => viewTicketDetail(ticket)}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1,
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight="bold">
                              #{ticket.ticketNumber}
                            </Typography>
                            <Typography variant="body1">
                              {ticket.subject}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                            <Chip
                              label={ticket.status}
                              color={getStatusColor(ticket.status)}
                              size="small"
                            />
                            <Chip
                              label={ticket.priority}
                              size="small"
                              variant="outlined"
                            />
                            <Typography variant="caption" sx={{ ml: "auto" }}>
                              {formatDate(ticket.updatedAt, "MMM dd, yyyy")}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* Create Ticket View */}
        {view === "create-ticket" && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <IconButton onClick={() => setView("my-tickets")} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">Create Support Ticket</Typography>
            </Box>

            <form onSubmit={handleCreateTicket}>
              <TextField
                fullWidth
                label="Subject"
                value={newTicketData.subject}
                onChange={(e) =>
                  setNewTicketData({
                    ...newTicketData,
                    subject: e.target.value,
                  })
                }
                required
                autoFocus
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Description"
                value={newTicketData.description}
                onChange={(e) =>
                  setNewTicketData({
                    ...newTicketData,
                    description: e.target.value,
                  })
                }
                required
                multiline
                rows={6}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                select
                label="Priority"
                value={newTicketData.priority}
                onChange={(e) =>
                  setNewTicketData({
                    ...newTicketData,
                    priority: e.target.value,
                  })
                }
                sx={{ mb: 3 }}
              >
                <MenuItem value="Low">Low</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Urgent">Urgent</MenuItem>
              </TextField>

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={createLoading}
                startIcon={createLoading && <CircularProgress size={20} />}
              >
                {createLoading ? "Creating..." : "Create Ticket"}
              </Button>
            </form>
          </Box>
        )}

        {/* Ticket Detail View */}
        {view === "ticket-detail" && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <IconButton onClick={() => setView("my-tickets")} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">Ticket Details</Typography>
            </Box>

            {ticketLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : ticketData?.getPublicTicket ? (
              <>
                {/* Ticket Info */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      #{ticketData.getPublicTicket.ticketNumber}
                    </Typography>
                    <Typography variant="h5" gutterBottom>
                      {ticketData.getPublicTicket.subject}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                      <Chip
                        label={ticketData.getPublicTicket.status}
                        color={getStatusColor(
                          ticketData.getPublicTicket.status
                        )}
                      />
                      <Chip
                        label={ticketData.getPublicTicket.priority}
                        variant="outlined"
                      />
                      {ticketData.getPublicTicket.assignedTo && (
                        <Chip
                          label={`Assigned: ${ticketData.getPublicTicket.assignedTo.name}`}
                        />
                      )}
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body1">
                      {ticketData.getPublicTicket.description}
                    </Typography>
                  </CardContent>
                </Card>

                {/* Messages */}
                <Typography variant="h6" gutterBottom>
                  Conversation
                </Typography>
                <List
                  sx={{ mb: 3, bgcolor: "background.paper", borderRadius: 1 }}
                >
                  {ticketData.getPublicTicket.messages.map((message, index) => (
                    <React.Fragment key={message.id || index}>
                      <ListItem alignItems="flex-start">
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" fontWeight="bold">
                              {message.senderName}
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ ml: 1, color: "text.secondary" }}
                              >
                                {formatDate(
                                  message.timestamp,
                                  "MMM dd, yyyy HH:mm"
                                )}
                              </Typography>
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2">
                              {message.content}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index <
                        ticketData.getPublicTicket.messages.length - 1 && (
                        <Divider />
                      )}
                    </React.Fragment>
                  ))}
                </List>

                {/* Reply Form */}
                <form onSubmit={handleAddMessage}>
                  <TextField
                    fullWidth
                    label="Reply to ticket"
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    multiline
                    rows={3}
                    disabled={addMessageLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            type="submit"
                            disabled={addMessageLoading || !replyMessage.trim()}
                            color="primary"
                          >
                            <SendIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </form>
              </>
            ) : null}
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default PublicSupportTicket;
